import type { RunStepRow, StepName, StepStatus } from "@shared/types/runs";

/**
 * One vocabulary for "Step n of m" (Session 10, ux-s10.md §0).
 *
 * **Steps are the run's own `run_steps` rows**, never the five `STEP_NAMES`: a standard import
 * of one connection has three rows (extract, load, finalize) and reads "of 3"; an incremental
 * one adds the merge and reads "of 4"; a schedule of three connections with transformations
 * has 3 x 2 + 1 + 1 rows and reads "of 8". Nothing pads to five, and queued is a state before
 * step 1, not a step.
 *
 * Two scopes, one rule each:
 * - **A connection surface** (the list row, the editor): this connection's rows plus the
 *   run-level rows (`transform`, `finalize`, whose `dataflowId` is null), the belongs-rule the
 *   run stepper has always used.
 * - **The run page**: every row of the run.
 *
 * The labels here are for sentences. The bare nouns (`STEP_LABELS` in `runFormatting.ts`) stay
 * for column headers.
 */

/** The chain order of a connection's steps; the run-level ones come last. */
export const CHAIN_ORDER: readonly StepName[] = [
  "extract",
  "load",
  "merge",
  "transform",
  "finalize",
];

export type DestinationKind = "snowflake" | "databricks";

/** What a sentence can name. Both are optional: a surface that does not know says less. */
export interface StepVocabularyContext {
  /** The connector's display name ("Paylocity"). */
  connector?: string | null;
  /** The instance's destination, when the screen knows it without an audited read. */
  destination?: DestinationKind | null;
  /**
   * Round 6, W5: the run holds no connection (a transform-only schedule, or Run on
   * Transformations), so it is not an import: its last step is "Finish the run".
   */
  transformOnly?: boolean;
  /**
   * #2389: each connection's connector display name by connection id ("Mouseflow"), for the
   * running extract's liveness line ("Still reading from Mouseflow."). A connection it does not
   * name reads "the source".
   */
  sourceNames?: ReadonlyMap<string, string>;
}

const DESTINATION_NAME: Record<DestinationKind, string> = {
  snowflake: "Snowflake",
  databricks: "Databricks",
};

function connectorName(ctx: StepVocabularyContext): string {
  const name = ctx.connector?.trim();
  return name ? name : "the source";
}

function destinationName(ctx: StepVocabularyContext): string {
  return ctx.destination ? DESTINATION_NAME[ctx.destination] : "the destination";
}

/** The done and pending label: "Extract from Paylocity", "Load into Snowflake". */
export function stepLabel(step: StepName, ctx: StepVocabularyContext = {}): string {
  switch (step) {
    case "extract":
      return `Extract from ${connectorName(ctx)}`;
    case "load":
      return `Load into ${destinationName(ctx)}`;
    case "merge":
      return "Merge changes";
    case "transform":
      return "Run dbt";
    case "finalize":
      return ctx.transformOnly ? "Finish the run" : "Finish the import";
  }
}

/** The short verb a list row shows: "Step 2 of 3 · Loading · 7 of 40 tables". */
export function stepVerb(step: StepName): string {
  switch (step) {
    case "extract":
      return "Extracting";
    case "load":
      return "Loading";
    case "merge":
      return "Merging";
    case "transform":
      return "Transforming";
    case "finalize":
      return "Finishing";
  }
}

/** Sub-progress a running sentence may carry; only a row that reports it gets it. */
export interface StepSubProgress {
  done: number | null;
  total: number | null;
}

/** "7 of 40 tables", "7 tables"; null when nothing is known. */
export function tablesPhrase(sub: StepSubProgress | null | undefined): string | null {
  if (sub?.done == null) return null;
  if (sub.total !== null && sub.total > 0) {
    return `${sub.done.toLocaleString()} of ${sub.total.toLocaleString()} tables`;
  }
  // No total yet and nothing done: "0 tables" would read as an empty import.
  if (sub.done === 0) return null;
  return `${sub.done.toLocaleString()} ${sub.done === 1 ? "table" : "tables"}`;
}

/**
 * The running sentence: "Extracting from Paylocity · 7 of 40 tables", "Loading into Snowflake",
 * "Running dbt · model 3 of 12" (models only when a total is known).
 */
export function runningSentence(
  step: StepName,
  ctx: StepVocabularyContext = {},
  sub?: StepSubProgress | null,
): string {
  let head: string;
  switch (step) {
    case "extract":
      head = `Extracting from ${connectorName(ctx)}`;
      break;
    case "load":
      head = `Loading into ${destinationName(ctx)}`;
      break;
    case "merge":
      return "Merging changes";
    case "transform":
      return sub && sub.done !== null && sub.total !== null && sub.total > 0
        ? `Running dbt · model ${sub.done} of ${sub.total}`
        : "Running dbt";
    case "finalize":
      return "Finishing the import";
  }
  const tables = tablesPhrase(sub);
  return tables ? `${head} · ${tables}` : head;
}

/** "Step 2 of 3". */
export function stepOfLine(index: number, total: number): string {
  return `Step ${index} of ${total}`;
}

const TERMINAL: ReadonlySet<StepStatus> = new Set(["succeeded", "failed", "skipped", "cancelled"]);

export function isTerminalStep(status: StepStatus | null | undefined): boolean {
  return status !== null && status !== undefined && TERMINAL.has(status);
}

function chainRank(step: StepName): number {
  return CHAIN_ORDER.indexOf(step);
}

/**
 * The rows a connection surface counts: this connection's rows plus the run-level ones, in
 * chain order. One row per step name: a connection has at most one of each.
 */
export function connectionStepRows(steps: readonly RunStepRow[], dataflowId: string): RunStepRow[] {
  const byStep = new Map<StepName, RunStepRow>();
  for (const row of steps) {
    const belongs = row.dataflowId === dataflowId || row.dataflowId === null;
    if (belongs && !byStep.has(row.step)) byStep.set(row.step, row);
  }
  return [...byStep.values()].sort((a, b) => chainRank(a.step) - chainRank(b.step));
}

/**
 * Every row of a run in reading order: each connection's chain in the order the run lists its
 * connections, then the run-level rows (transform before finalize).
 */
export function runStepRows(
  steps: readonly RunStepRow[],
  dataflowIds: readonly string[] = [],
): RunStepRow[] {
  const connectionOrder = new Map<string, number>();
  dataflowIds.forEach((id, index) => connectionOrder.set(id, index));
  const orderOf = (row: RunStepRow): number => {
    if (row.dataflowId === null) return Number.MAX_SAFE_INTEGER;
    return connectionOrder.get(row.dataflowId) ?? dataflowIds.length;
  };
  return [...steps].sort(
    (a, b) =>
      orderOf(a) - orderOf(b) ||
      (a.dataflowId ?? "").localeCompare(b.dataflowId ?? "") ||
      chainRank(a.step) - chainRank(b.step),
  );
}

/**
 * The 1-based position of `step` in a connection's chain of `total` rows, for a surface that
 * knows only the count (a list row seeded from `lastRun`). A connection's chain is extract,
 * load, then the optional merge, then the run-level transform (optional) and finalize (always
 * last), so the position is decidable from the count alone: transform is second to last and
 * finalize last whatever sits between.
 */
export function connectionStepPosition(step: StepName, total: number): number {
  switch (step) {
    case "extract":
      return 1;
    case "load":
      return Math.min(2, total);
    case "merge":
      return Math.min(3, total);
    case "transform":
      return Math.max(1, total - 1);
    case "finalize":
      return total;
  }
}

/**
 * #2389: each connection's connector display name by connection id, from the screen's own
 * connection list and the connector catalogue, for `StepVocabularyContext.sourceNames`. A
 * connection whose connector the catalogue does not hold is left out ("the source").
 */
export function connectorNamesByConnection(
  connections: ReadonlyArray<{ id: string; connectorName: string }> | undefined,
  connectors: ReadonlyArray<{ name: string; displayName: string }> | undefined,
): ReadonlyMap<string, string> {
  const names = new Map<string, string>();
  if (!connections || !connectors) return names;
  const byConnector = new Map(connectors.map((connector) => [connector.name, connector]));
  for (const connection of connections) {
    const displayName = byConnector.get(connection.connectorName)?.displayName.trim();
    if (displayName) names.set(connection.id, displayName);
  }
  return names;
}
